import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance, { getAPIURL } from "../../../api/axiosInstance";
import { useAppContext } from "../../../Context/AppContext";
import { useMakePayment } from "../../../api/mutations/useMakePayment";
import { previewWalletPricing } from "../../../api/mutations/usePreviewWalletPricing";
import { runWalletPaymentRequest } from "../../../utils/stripeExpressWalletPay";
import { useGetBilling } from "../../../api/queries/useGetBilling";
import { getSavedCards } from "../../../utils/savedPaymentMethods";
import { useGetInvestorDetails } from "../../../api/queries/MuslimInvest/useGetInvestorDetails";
import { useGetMuslimInvestPaymentProducts } from "../../../api/queries/MuslimInvest/useGetMuslimInvestPaymentProducts";
import { toast } from "react-toastify";
import { useSaveInvestor } from "../../../api/mutations/MuslimInvest/useSaveInvestor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { FcGoogle } from "react-icons/fc";
import {
  logPaymentError,
  getPaymentErrorSource,
  getPaymentErrorMessage,
} from "../../../utils/paymentErrorLogger";
import {
  getUnsupportedWalletMessage,
  getWalletButtonVisibility,
} from "../../../utils/walletSupport";
import { completeStripePaymentFromIntent } from "../../../utils/completeStripePaymentIntent";
import { useTranslation } from "react-i18next";

const PRICE = "500.00";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#000",
      "::placeholder": { color: "#999" },
    },
    invalid: { color: "red" },
  },
};

const CARD_EXPIRY_CVC_OPTIONS = {
  style: {
    base: {
      fontSize: "16px",
      color: "#000",
      "::placeholder": { color: "#999" },
    },
  },
};

const InvestorCheckout = memo(function InvestorCheckout() {
  const { t } = useTranslation();
  const { objectId, user } = useAppContext();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const [billingAddress, setBillingAddress] = useState("");
  const [useSavedMethod, setUseSavedMethod] = useState(false);
  const navigate = useNavigate();
  const { mutate, mutateAsync: payInvestorAsync } = useMakePayment();
  const queryClient = useQueryClient();
  const { data: investorData } = useGetInvestorDetails({
    userId: user?.user.id.toString() || "",
  });
  const {
    data: paymentProducts,
    isLoading: paymentProductsLoading,
    isError: paymentProductsError,
  } = useGetMuslimInvestPaymentProducts();

  const productId = paymentProducts?.investorRegistrationProductId ?? "";
  const paymentReady = Boolean(productId) && !paymentProductsLoading;

  const profile = investorData ? investorData[0] : null;

  const { mutate: saveInvestorDetailsMutation } = useSaveInvestor({
    onSuccess: () => {
      // toast.success("Investor details saved successfully");
      queryClient.invalidateQueries({ queryKey: ["investor-data"] });
      navigate("/investing", { replace: true });
    },
    onError: () => {
      toast.error(t("common.unableProcessTryLater"));
    },
  });

  const { data: paymentMethods } = useGetBilling({ userId: objectId || "" });
  const savedCards = useMemo(() => getSavedCards(paymentMethods), [paymentMethods]);
  const { showApplePay, showGooglePay } = getWalletButtonVisibility();

  useEffect(() => {
    if (savedCards.length > 0) {
      setUseSavedMethod(true);
    }
  }, [savedCards.length]);

  const saveProfileInvestorDetails = useCallback(() => {
    if (!profile) {
      toast.error(t("muslimInvest.investorProfileNotFound"));
      setLoading(false);
      return;
    }

    setLoading(true);
    const formData = new FormData();

    Object.entries(profile).forEach(([key, value]) => {
      if (key === "id" && typeof value === "number" && value <= 0) return;
      if (value !== undefined && value !== null)
        formData.append(key, String(value));
    });

    formData.set("status", "Registered");
    saveInvestorDetailsMutation(formData);
  }, [profile, saveInvestorDetailsMutation, t]);

  useEffect(() => {
    if (!objectId) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    setLoading(true);
    axiosInstance
      .post(getAPIURL("/Payment/verify-checkout-session"), { sessionId })
      .then(() => {
        saveProfileInvestorDetails();
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((err: unknown) => {
        setError(getPaymentErrorMessage(err));
      })
      .finally(() => setLoading(false));
  }, [objectId, saveProfileInvestorDetails]);

  const handleHostedCheckout = useCallback(
    async (preferredWallet?: "apple_pay" | "google_pay") => {
      if (!preferredWallet) return;

      const walletMessage = getUnsupportedWalletMessage(preferredWallet);
      if (walletMessage) {
        setError(walletMessage);
        return;
      }

      if (!objectId || !stripe || !productId) {
        if (!paymentProductsLoading && !productId) {
          setError(t("muslimInvest.paymentProductsNotConfigured"));
        }
        return;
      }
      setLoading(true);
      setError("");
      try {
        const preview = await previewWalletPricing({
          userId: objectId,
          productId,
          discountCode: "",
          isAnnual: false,
          isEvent: false,
          isEventEarlyBird: false,
          name: `${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim(),
          email: user?.user?.email || "",
        });

        if (preview.noPaymentRequired) {
          setError("No payment is due. Please use Pay Now below.");
          setLoading(false);
          return;
        }

        await runWalletPaymentRequest({
          stripe,
          amountMinor: preview.amountMinor,
          currency: preview.currency,
          label: preview.label,
          preferredWallet,
          onPaymentMethod: async (pm) => {
            const response = await payInvestorAsync({
              paymentMethodTypes: ["card"],
              customerId: "",
              paymentMethodId: pm.id,
              userId: objectId || "",
              name: `${user?.user?.firstName || ""} ${
                user?.user?.lastName || ""
              }`.trim(),
              email: user?.user?.email || "",
              productId,
              discountCode: "",
              isEvent: false,
            });

            const confirmResult = await completeStripePaymentFromIntent(
              stripe,
              response,
              pm.id
            );
            if (!confirmResult.success) {
              logPaymentError({
                flow: "investor-checkout",
                errorSource: "stripe",
                message: confirmResult.message,
                responseData: response,
              });
              throw new Error(confirmResult.message);
            }
            saveProfileInvestorDetails();
          },
        });
      } catch (err: unknown) {
        const msg = getPaymentErrorMessage(err);
        if (err instanceof Error && err.message === "cancelled") {
          setError("");
        } else {
          setError(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      objectId,
      user,
      stripe,
      productId,
      paymentProductsLoading,
      payInvestorAsync,
      saveProfileInvestorDetails,
      t,
    ],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      const msg =
        "The secure payment form is still loading. Please wait a moment and try again.";
      logPaymentError({
        flow: "investor-checkout",
        errorSource: "frontend",
        message: msg,
      });
      setError(msg);
      return;
    }

    if (!productId) {
      setError(t("muslimInvest.paymentProductsNotConfigured"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const cardNumberElement = elements?.getElement(CardNumberElement);

      if (!stripe || !elements) {
        const msg =
          "The secure payment form is still loading. Please wait a moment and try again.";
        logPaymentError({
          flow: "investor-checkout",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        setLoading(false);
        return;
      }

      let newPaymentMethod;
      if (!useSavedMethod && cardNumberElement) {
        const { paymentMethod, error: pmError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardNumberElement,
          });
        if (pmError || !paymentMethod) {
          const msg = pmError?.message || t("events.cardCreationFailed");
          logPaymentError({
            flow: "investor-checkout",
            errorSource: "stripe",
            message: msg,
            error: pmError,
          });
          setError(msg);
          setLoading(false);
          return;
        }
        newPaymentMethod = paymentMethod;
      }

      const selectedPaymentMethodId =
        useSavedMethod && savedCards[0]?.paymentMethodId
          ? savedCards[0].paymentMethodId
          : newPaymentMethod?.id;

      if (!selectedPaymentMethodId) {
        const msg = t("events.noValidPaymentMethod");
        logPaymentError({
          flow: "investor-checkout",
          errorSource: "frontend",
          message: msg,
        });
        setError(msg);
        setLoading(false);
        return;
      }

      mutate(
        {
          paymentMethodTypes: ["card"],
          customerId: "",
          paymentMethodId: selectedPaymentMethodId,
          userId: objectId || "",
          name: `${user?.user?.firstName || ""} ${
            user?.user?.lastName || ""
          }`.trim(),
          email: user?.user?.email || "",
          productId,
          discountCode: "",
          isEvent: false,
        },
        {
          onSuccess: async (response) => {
            if (!stripe) {
              setLoading(false);
              return;
            }

            const confirmResult = await completeStripePaymentFromIntent(
              stripe,
              response,
              selectedPaymentMethodId
            );

            if (!confirmResult.success) {
              logPaymentError({
                flow: "investor-checkout",
                errorSource: "stripe",
                message: confirmResult.message,
                responseData: response,
              });
              setError(confirmResult.message);
              setLoading(false);
              return;
            }

            saveProfileInvestorDetails();
            setLoading(false);
          },
          onError: (err: unknown) => {
            const source = getPaymentErrorSource(err);
            const msg = getPaymentErrorMessage(err);
            logPaymentError({
              flow: "investor-checkout",
              errorSource: source,
              message: msg,
              error: err,
              responseStatus:
                err && typeof err === "object" && "response" in err
                  ? (err as { response?: { status?: number } }).response?.status
                  : undefined,
              responseData:
                err && typeof err === "object" && "response" in err
                  ? (err as { response?: { data?: unknown } }).response?.data
                  : undefined,
            });
            setError(msg);
            setLoading(false);
          },
        }
      );
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : t("events.unexpectedError");
      logPaymentError({
        flow: "investor-checkout",
        errorSource: "frontend",
        message: msg,
        error: err,
      });
      setError(msg);
      setLoading(false);
    }
    },
    [
      stripe,
      elements,
      useSavedMethod,
      savedCards,
      mutate,
      objectId,
      user,
      saveProfileInvestorDetails,
      productId,
      t,
    ]
  );

  const handleUseSavedCard = useCallback(() => setUseSavedMethod(true), []);
  const handleUseNewCard = useCallback(() => setUseSavedMethod(false), []);
  const handleBillingAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setBillingAddress(e.target.value),
    []
  );

  return (
    <div className="m-4 rounded-4 p-4 shadow border-0">
      <div className="row">
        {/* LEFT SIDE — PAYMENT FORM */}
        <div className="col d-flex flex-column">
          <h3 className="fw-bold mb-3">{t("muslimInvest.investorRegistrationCheckout")}</h3>

          {(paymentProductsError || (!paymentProductsLoading && !productId)) && (
            <div className="alert alert-warning">
              {t("muslimInvest.paymentProductsNotConfigured")}
            </div>
          )}

          <div className="alert alert-info p-3">
            <p className="mb-2">
              {t("muslimInvest.investorCheckoutIntro1")}
            </p>
            <p className="mb-2">
              {t("muslimInvest.investorCheckoutIntro2")}
            </p>
            <p className="fw-bold mb-0 text-danger">
              {t("muslimInvest.paymentNonRefundable")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="w-100" autoComplete="off">
            <div className="d-flex flex-column gap-3">
              <div className="border rounded-4 p-3 shadow-sm bg-light">
                <h5 className="fw-bold mb-3">{t("muslimInvest.bookingSummary")}</h5>

                <div className="d-flex justify-content-between mb-2">
                  <span>{t("muslimInvest.investorRegistration")}</span>
                  <span className="fw-bold">£{PRICE}</span>
                </div>

                <hr />

                <div className="d-flex justify-content-between">
                  <span>{t("events.total")}</span>
                  <span className="fw-bold fs-5">£{PRICE}</span>
                </div>
              </div>

              <div className="rounded-3 p-2 bg-white">
                <div className="d-flex align-items-center justify-content-start gap-3">
                  {showApplePay && (
                  <button
                    type="button"
                    className="btn btn-dark border border-secondary text-white fw-semibold d-flex align-items-center justify-content-center flex-fill rounded-4 px-4 py-3 shadow-sm"
                    onClick={() => void handleHostedCheckout("apple_pay")}
                    disabled={loading || !objectId || !paymentReady}
                  >
                    <span className="d-inline-flex align-items-center justify-content-center gap-2">
                      <span className="d-inline-flex align-items-center justify-content-center gap-1">
                        <FontAwesomeIcon
                          icon={faApple}
                          className="fs-4"
                        />
                        <span className="fs-3 fw-medium lh-1 text-white">
                          {t("muslimInvest.pay")}
                        </span>
                      </span>
                    </span>
                  </button>
                  )}
                  {showGooglePay && (
                  <button
                    type="button"
                    className="btn btn-light bg-white border border-secondary-subtle fw-semibold d-flex align-items-center justify-content-center flex-fill rounded-4 px-4 py-3 shadow-sm"
                    onClick={() => void handleHostedCheckout("google_pay")}
                    disabled={loading || !objectId || !paymentReady}
                  >
                    <span className="d-inline-flex align-items-center justify-content-center gap-2">
                      <span className="d-inline-flex align-items-center justify-content-center gap-1">
                        <FcGoogle className="fs-4" />
                        <span className="fs-3 fw-medium lh-1 text-dark">
                          {t("muslimInvest.pay")}
                        </span>
                      </span>
                    </span>
                  </button>
                  )}
                </div>
              </div>

              <div className="small text-muted">{t("muslimInvest.orPayWithCardBelow")}</div>

              {savedCards.length > 0 ? (
                <div className="mb-3">
                  <label className="form-label">{t("events.choosePaymentMethod")}</label>

                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="savedCard"
                      checked={useSavedMethod}
                      onChange={handleUseSavedCard}
                    />
                    <label className="form-check-label" htmlFor="savedCard">
                      Use saved card (**** **** **** {savedCards[0].last4}
                      , {savedCards[0].brand})
                    </label>
                  </div>

                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      id="newCard"
                      checked={!useSavedMethod}
                      onChange={handleUseNewCard}
                    />
                    <label className="form-check-label" htmlFor="newCard">
                      {t("events.useDifferentCard")}
                    </label>
                  </div>
                </div>
              ) : null}

              {!useSavedMethod && (
                <div className="row g-2">
                  <div className="col-md-6 mb-3">
                    <label>{t("events.cardNumber")}</label>
                    <div className="form-control">
                      <CardNumberElement options={CARD_ELEMENT_OPTIONS} />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label>{t("events.expiry")}</label>
                    <div className="form-control">
                      <CardExpiryElement options={CARD_EXPIRY_CVC_OPTIONS} />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label>{t("events.cvc")}</label>
                    <div className="form-control">
                      <CardCvcElement options={CARD_EXPIRY_CVC_OPTIONS} />
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>{t("events.billingAddressOptional")}</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("events.billingAddress")}
                      value={billingAddress}
                      autoComplete="new-address"
                      onChange={handleBillingAddressChange}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger py-2 px-3" role="alert">
                  {error}
                </div>
              )}

              <div className="d-flex justify-content-between mt-4">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!stripe || loading}
                >
                  {loading ? t("events.processing") : t("events.payNow")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
});

InvestorCheckout.displayName = "InvestorCheckout";

export default InvestorCheckout;
